import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Award, Target, Eye, Heart } from "lucide-react";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AboutPage() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    document.title = "About Us - 30+ Years of Excellence | Amar Preet Scaffolding";
    axios.get(`${API}/stats`).then(res => setStats(res.data)).catch(console.error);
  }, []);

  const milestones = [
    { year: "1994", title: "Founded", description: "Amar Preet Group established in Delhi" },
    { year: "2005", title: "Expansion", description: "Expanded operations to Noida and NCR" },
    { year: "2015", title: "ISO Certified", description: "Achieved ISO 9001:2015 certification" },
    { year: "2018", title: "Hyderabad", description: "Expanded to Hyderabad" },
    { year: "2024", title: "141+ Projects", description: "Continuing to grow and serve" },
    { year: "2026", title: "Vizag", description: "Expanded to Vizag" },
  ];

  return (
    <div data-testid="about-page">
      <SEO title="About Us" description="30+ years of scaffolding excellence. Amar Preet Scaffolding serves 200+ clients across India with ISO certified scaffolding and shuttering rental solutions." canonical="/about" />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="about-headline">
            About Us
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            30+ Years of Excellence in Scaffolding Solutions
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="text-yellow-500 font-semibold text-sm tracking-wider uppercase">Our Story</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-4 mb-6">
                Building Trust, <span className="text-yellow-500">One Project at a Time</span>
              </h2>
              <div className="space-y-6 text-slate-600 leading-relaxed">
                <p>
                  With an industrial experience of 30 years, Amar Preet Scaffolding—a part of AMAR PREET GROUP and a sister concern of Amar Preet Shutterings—has achieved a respectable stand in the market along with our unmatched services in construction building.
                </p>
                <p>
                  We are well known suppliers of Scaffolding and Shuttering in India like Cuplocks, Jacks, Joint Pins, Couplers, Mild Steel Pipes, Steel Plates, Planks, Acrospan, Props, Channels, Steel Challis and more.
                </p>
                <p>
                  Serving with a will to attain maximum client satisfaction, our scaffolding and shuttering products are internationally acclaimed for cost effectiveness and flexibility in execution.
                </p>
              </div>

              <div className="mt-10 space-y-4">
                {[
                  "Well equipped infrastructure for production",
                  "Efficient platforms for construction and industrial maintenance",
                  "Stringent quality standards of international standards",
                  "Research & Development facility for constant innovation",
                  "On time delivery with reliable track record"
                ].map((feature) => (
                  <div key={feature} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <img
                src="/images/about.jpg"
                alt="Amar Preet Scaffolding facility"
                className="w-full h-[400px] object-cover rounded-2xl shadow-2xl mb-8"
              />
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-xl p-6 text-center">
                  <p className="text-4xl font-bold text-yellow-500">{stats?.projects_completed || 141}+</p>
                  <p className="text-sm text-slate-600">Projects</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-6 text-center">
                  <p className="text-4xl font-bold text-yellow-500">30+</p>
                  <p className="text-sm text-slate-600">Years</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-6 text-center">
                  <p className="text-4xl font-bold text-yellow-500">{stats?.satisfied_clients || 221}+</p>
                  <p className="text-sm text-slate-600">Clients</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-6 text-center">
                  <p className="text-4xl font-bold text-yellow-500">140+</p>
                  <p className="text-sm text-slate-600">Workers</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Target, title: "Our Mission", description: "To provide world-class scaffolding solutions that ensure safety, efficiency, and excellence in every construction project we support." },
              { icon: Eye, title: "Our Vision", description: "To be India's most trusted and innovative scaffolding solutions provider, setting new standards in construction safety and quality." },
              { icon: Heart, title: "Our Values", description: "Safety first, customer satisfaction, innovation, integrity, and commitment to excellence in everything we do." },
            ].map((value) => (
              <div key={value.title} className="bg-white rounded-2xl p-8 shadow-lg" data-testid={`value-${value.title.toLowerCase().replace(' ', '-')}`}>
                <value.icon className="w-12 h-12 text-yellow-500 mb-6" />
                <h3 className="text-xl font-bold text-slate-900 mb-4">{value.title}</h3>
                <p className="text-slate-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Our Journey</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {milestones.map((milestone, index) => (
              <div key={milestone.year} className="text-center" data-testid={`milestone-${index}`}>
                <div className="w-16 h-16 bg-yellow-500 rounded-full mx-auto mb-3 flex items-center justify-center">
                  <span className="text-lg font-bold text-slate-900">{milestone.year}</span>
                </div>
                <h3 className="font-bold text-white text-sm mb-1">{milestone.title}</h3>
                <p className="text-xs text-slate-400">{milestone.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Certifications & Awards</h2>
            <p className="text-slate-600">Recognized for quality and excellence</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-slate-50 rounded-xl p-4">
              <img src="/images/certificates/iso-9001-2015.jpeg" alt="ISO 9001:2015 Certification" className="w-full h-48 object-contain" />
              <p className="text-center font-medium mt-4">ISO 9001:2015</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <img src="/images/certificates/iso-45001-2018.jpeg" alt="ISO 45001:2018 Certification" className="w-full h-48 object-contain" />
              <p className="text-center font-medium mt-4">ISO 45001:2018</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <img src="/images/new/11.jpg" alt="Entrepreneur Award" className="w-full h-48 object-cover rounded-lg" />
              <p className="text-center font-medium mt-4">Entrepreneur Award</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <img src="/images/new/12.jpg" alt="Visionary Award" className="w-full h-48 object-cover rounded-lg" />
              <p className="text-center font-medium mt-4">Visionary Award</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-yellow-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Partner with India's Trusted Scaffolding Provider
          </h2>
          <Link
            to="/contact"
            className="inline-block bg-slate-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-slate-800 transition-all duration-300 hover:scale-105"
            data-testid="about-cta"
          >
            Get in Touch
          </Link>
        </div>
      </section>
    </div>
  );
}
